import React, { useState, useEffect } from 'react';
import { FiPieChart, FiList, FiClock, FiCheck, FiRotateCcw, FiActivity, FiSearch, FiChevronDown, FiChevronUp, FiEye, FiSettings, FiCalendar, FiBox, FiAlertCircle, FiArrowLeft, FiFileText, FiXCircle } from 'react-icons/fi';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { useDispatch, useSelector } from 'react-redux';
import { getAdvanceLeaves, updateLeaveStatus } from '../../../Redux/Slice/LeaveForm';
import Cookies from 'js-cookie';
import { format, isSameDay, addDays, getDay, subMonths } from 'date-fns';
import Loader from '../../../Components/Loader/Loader';
import { TablePagination } from "@mui/material";
import socket from '../../../socket';

const colorMap = {
    'Emergency Leave': '#e11d48', // rose-600
    'Advance Leave': '#1e3a8a', // blue-900
    'Time Break': '#059669', // emerald-600
    'Early Leave': '#7c3aed', // violet-600
    'Half Day Leave': '#d97706', // amber-600
    'Late Arrival': '#ea580c', // orange-600
    'Leave': '#64748b' // slate-500
};

const calculateWorkingDays = (startDate, endDate) => {
    if (!startDate || !endDate) return 0;
    let count = 0;
    let curr = new Date(startDate);
    const end = new Date(endDate);
    while (curr <= end) {
        if (getDay(curr) !== 0) {
            count++;
        }
        curr = addDays(curr, 1);
    }
    return count;
};

const MyRequest = () => {
    const dispatch = useDispatch();

    const loginDataStr = Cookies.get("loginData");
    let loginData = null;
    if (loginDataStr) {
        loginData = JSON.parse(loginDataStr);
    }
    const applicantUser = loginData?.data || loginData?.user || {};

    const { leaveData, listLoading } = useSelector((state) => state.LeaveForm);

    useEffect(() => {
        socket.on("leaveUpdate", (data) => {
            console.log("Live leave update received (Employee):", data);
            if (applicantUser?._id) {
                dispatch(getAdvanceLeaves(applicantUser._id));
            }
        });

        return () => {
            socket.off("leaveUpdate");
        };
    }, [dispatch, applicantUser?._id]);

    const [activeTab, setActiveTab] = useState('Overview');
    const [expandedRequest, setExpandedRequest] = useState(null);

    const [searchQuery, setSearchQuery] = useState('');
    const [typeFilter, setTypeFilter] = useState('All Types');
    const [statusFilter, setStatusFilter] = useState('Any Status');
    const [selectedMonth, setSelectedMonth] = useState('All Months');

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const monthOptions = Array.from({ length: 12 })
        ?.map((_, i) => {
            const m = subMonths(new Date(), i);
            return {
                value: format(m, 'yyyy-MM'),
                label: format(m, 'MMMM yyyy')
            };
        })
        ?.filter(opt => opt.value >= '2026-02');

    useEffect(() => {
        if (applicantUser?._id) {
            dispatch(getAdvanceLeaves(applicantUser._id));
        }
    }, [dispatch, applicantUser?._id]);

    const myLeaves = leaveData?.filter(
        leave => String(leave.applicant?._id || leave.applicant) === String(applicantUser?._id)
    ) || [];

    const mappedRequestsList = myLeaves.map((leave) => {
        return {
            id: leave._id,
            displayId: leave._id ? `LR${leave._id.slice(-4).toUpperCase()}` : 'LR0001',
            type: leave.leaveReason || "Leave",
            TotalDays: leave.TotalDays,
            halfType: leave.halfType,
            leavingTime: leave.leavingTime,
            duration: leave.LeaveType === "Early Leave"
                ? `Early (${leave.leavingTime})`
                : leave.LeaveType === "Late Arrival"
                    ? `Late (${leave.arrivalTime})`
                    : leave.LeaveType === "Time Break"
                        ? `${leave.leavingTime} - ${leave.arrivalTime}`
                        : leave.TotalDays !== undefined
                            ? `${leave.TotalDays} Day${leave.TotalDays === 1 ? '' : 's'}${leave.halfType ? ` (${leave.halfType})` : ''}`
                            : `${calculateWorkingDays(leave.fromDate, leave.toDate)} Day${calculateWorkingDays(leave.fromDate, leave.toDate) === 1 ? '' : 's'}`,
            dateRange: leave.fromDate && leave.toDate
                ? (isSameDay(new Date(leave.fromDate), new Date(leave.toDate))
                    ? format(new Date(leave.fromDate), 'dd MMM')
                    : `${format(new Date(leave.fromDate), 'dd MMM')} to ${format(new Date(leave.toDate), 'dd MMM')}`)
                : format(new Date(leave.fromDate), 'dd MMM yyyy'),
            dateSub: leave.fromDate && leave.toDate ? `${format(new Date(leave.fromDate), 'yyyy')}` : 'Single Day',
            status: (leave.Status === 'Approved' && leave.approvers?.some(app => app.status === 'Approved by Owner')) 
                    ? 'DIRECTLY APPROVED BY OWNER' 
                    : (leave.Status ? leave.Status.toUpperCase() : 'PENDING'),
            appliedOn: format(new Date(leave.createdAt), 'dd MMM yyyy'),
            reason: leave.reason || "Leave request pending.",
            LeaveType: leave.LeaveType || "Leave",
            approvers: (leave.approvers || []).map((app, index) => {
                let status = 'awaiting previous step';
                if (app.status) status = app.status;
                return {
                    role: app.position ? app.position.toUpperCase() : `APPROVER ${index + 1}`,
                    name: app.name || 'Approver',
                    status: status.toLowerCase()
                };
            }),
            rawDate: leave.createdAt || leave.fromDate
        };
    });

    const filteredRequestsList = mappedRequestsList.filter(req => {
        const matchesSearch =
            req.displayId.toLowerCase().includes(searchQuery.toLowerCase()) ||
            (req.reason && req.reason.toLowerCase().includes(searchQuery.toLowerCase())) ||
            (req.LeaveType && req.LeaveType.toLowerCase().includes(searchQuery.toLowerCase()));

        const matchesType = typeFilter === 'All Types' || req.LeaveType === typeFilter;
        const matchesStatus = statusFilter === 'Any Status' || req.status.toLowerCase() === statusFilter.toLowerCase();

        let matchesMonth = true;
        if (selectedMonth !== 'All Months' && req.rawDate) {
            matchesMonth = format(new Date(req.rawDate), 'yyyy-MM') === selectedMonth;
        }

        return matchesSearch && matchesType && matchesStatus && matchesMonth;
    });

    const recentRequest = mappedRequestsList.length > 0 ? mappedRequestsList[0] : null;

    const [isWithdrawModalOpen, setIsWithdrawModalOpen] = useState(false);
    const [withdrawRequest, setWithdrawRequest] = useState({ id: null, type: null });

    const openWithdrawModal = (id, type) => {
        setWithdrawRequest({ id, type });
        setIsWithdrawModalOpen(true);
    };

    const confirmWithdraw = async () => {
        if (withdrawRequest?.id) {
            await dispatch(updateLeaveStatus({ id: withdrawRequest.id, action: 'Withdraw' }));
            if (applicantUser?._id) {
                dispatch(getAdvanceLeaves(applicantUser._id));
            }
        }
        closeWithdrawModal();
    };

    const closeWithdrawModal = () => {
        setIsWithdrawModalOpen(false);
        setWithdrawRequest({ id: null, type: null });
    };

    const toggleExpand = (id) => {
        if (expandedRequest === id) setExpandedRequest(null);
        else setExpandedRequest(id);
    };

    const totalRequests = mappedRequestsList.length;
    const pendingRequests = mappedRequestsList.filter(req => req.status === 'PENDING').length;
    const approvedRequests = mappedRequestsList.filter(req => req.status === 'APPROVED' || req.status === 'DIRECTLY APPROVED BY OWNER').length;

    const leaveCounts = mappedRequestsList.reduce((acc, current) => {
        const type = current.LeaveType || "Leave";
        acc[type] = (acc[type] || 0) + 1;
        return acc;
    }, {});

    const dynamicChartData = Object.keys(leaveCounts).map(type => ({
        name: type,
        value: leaveCounts[type],
        color: colorMap[type] || '#d1d5db'
    }));

    if (listLoading) {
        return <Loader />;
    }

    return (
        <div className="px-3 py-6 min-h-0 bg-gradient-to-br from-slate-50/50 via-white to-slate-50/20 font-sans">
            <div className="w-full space-y-6">
                {/* Page Title */}
                <div className="title mb-4">
                    <h3 className="text-xl font-bold main_title_page text-white px-4 py-2 inline-block rounded">
                        My Leave Dashboard
                    </h3>
                </div>

                {/* Styled Pills Tab Switcher */}
                <div className="flex bg-slate-100 p-1 rounded-xl w-fit shadow-inner border border-slate-200/50">
                    <button
                        onClick={() => setActiveTab('Overview')}
                        className={`flex items-center gap-2 px-5 py-2 rounded-lg text-xs font-semibold transition-all tracking-wider uppercase ${activeTab === 'Overview'
                            ? 'bg-[#133A69] text-white shadow-md'
                            : 'text-slate-650 hover:text-slate-800 hover:bg-white/40'
                            }`}
                    >
                        <FiActivity size={14} />
                        Overview
                    </button>
                    <button
                        onClick={() => setActiveTab('All Requests')}
                        className={`flex items-center gap-2 px-5 py-2 rounded-lg text-xs font-semibold transition-all tracking-wider uppercase ${activeTab === 'All Requests'
                            ? 'bg-[#133A69] text-white shadow-md'
                            : 'text-slate-650 hover:text-slate-800 hover:bg-white/40'
                            }`}
                    >
                        <FiList size={14} />
                        All Requests ({totalRequests})
                    </button>
                </div>

                {activeTab === 'Overview' && (
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">

                        {/* Left Side: Summary Metrics & Chart */}
                        <div className="lg:col-span-8 space-y-8">

                             {/* Summary Metrics Cards */}
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                                {/* Total Requests Card */}
                                <div className="bg-gradient-to-br from-white to-slate-50/70 border border-slate-150 rounded-2xl p-5 shadow-[0_15px_40px_rgba(0,0,0,0.02)] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md group">
                                    <div className="flex items-center justify-between mb-3">
                                        <span className="text-xs font-bold text-slate-700 uppercase tracking-widest">Total Applications</span>
                                        <span className="p-2.5 bg-blue-50 text-blue-600 rounded-xl group-hover:scale-105 transition-transform shadow-xs border border-blue-100/50">
                                            <FiBox size={16} />
                                        </span>
                                    </div>
                                    <div className="flex items-baseline gap-1 mt-1">
                                        <span className="text-3xl font-bold text-slate-900 tracking-tight">{totalRequests}</span>
                                        <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Total</span>
                                    </div>
                                </div>

                                {/* Pending Requests Card */}
                                <div className="bg-gradient-to-br from-white to-slate-50/70 border border-slate-150 rounded-2xl p-5 shadow-[0_15px_40px_rgba(0,0,0,0.02)] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md group">
                                    <div className="flex items-center justify-between mb-3">
                                        <span className="text-xs font-bold text-amber-900 uppercase tracking-widest">Pending Review</span>
                                        <span className="p-2.5 bg-amber-50 text-amber-600 rounded-xl group-hover:scale-105 transition-transform shadow-xs border border-amber-150">
                                            <FiClock size={16} />
                                        </span>
                                    </div>
                                    <div className="flex items-baseline gap-1 mt-1">
                                        <span className="text-3xl font-bold text-slate-900 tracking-tight">{pendingRequests}</span>
                                        <span className="text-[10px] font-bold text-amber-700 uppercase tracking-wider">In Process</span>
                                    </div>
                                </div>

                                {/* Approved Requests Card */}
                                <div className="bg-gradient-to-br from-white to-slate-50/70 border border-slate-150 rounded-2xl p-5 shadow-[0_15px_40px_rgba(0,0,0,0.02)] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md group">
                                    <div className="flex items-center justify-between mb-3">
                                        <span className="text-xs font-bold text-emerald-900 uppercase tracking-widest">Approved Leaves</span>
                                        <span className="p-2.5 bg-emerald-50 text-emerald-600 rounded-xl group-hover:scale-105 transition-transform shadow-xs border border-emerald-150">
                                            <FiCheck size={16} />
                                        </span>
                                    </div>
                                    <div className="flex items-baseline gap-1 mt-1">
                                        <span className="text-3xl font-bold text-slate-900 tracking-tight">{approvedRequests}</span>
                                        <span className="text-[10px] font-bold text-emerald-700 uppercase tracking-wider">Approved</span>
                                    </div>
                                </div>
                            </div>

                            {/* Consumption Mix (Pie chart) */}
                            <div className="bg-gradient-to-br from-white to-slate-50/70 border border-slate-100 rounded-2xl p-6 shadow-[0_15px_40px_rgba(0,0,0,0.03)] flex flex-col">
                                <div className="flex justify-between items-center mb-8 border-b border-slate-100 pb-5">
                                    <div className="flex items-center gap-3">
                                        <span className="p-2 bg-blue-50/80 text-blue-600 rounded-xl flex items-center justify-center shadow-sm">
                                            <FiPieChart size={18} />
                                        </span>
                                        <div>
                                            <h3 className="text-lg font-black text-slate-800 tracking-tight">Leave Consumption Mix</h3>
                                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mt-0.5">Distribution of requested leave categories</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex-grow flex flex-col md:flex-row items-center gap-8 md:gap-12">
                                    {/* Responsive Chart Container */}
                                    <div className="w-full md:w-1/2 relative h-[280px]">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <PieChart>
                                                <Pie
                                                    data={dynamicChartData.length > 0 ? dynamicChartData : [{ name: 'No Data', value: 1, color: '#f1f5f9' }]}
                                                    cx="50%"
                                                    cy="50%"
                                                    innerRadius={75}
                                                    outerRadius={105}
                                                    paddingAngle={5}
                                                    dataKey="value"
                                                    stroke="none"
                                                    cornerRadius={6}
                                                >
                                                    {(dynamicChartData.length > 0 ? dynamicChartData : [{ name: 'No Data', value: 1, color: '#f1f5f9' }]).map((entry, index) => (
                                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                                    ))}
                                                </Pie>
                                                <Tooltip
                                                    contentStyle={{ borderRadius: '16px', border: '1px solid #f1f5f9', boxShadow: '0 10px 30px rgba(0,0,0,0.05)', padding: '10px 16px' }}
                                                    itemStyle={{ fontWeight: '800', fontSize: '12px' }}
                                                />
                                            </PieChart>
                                        </ResponsiveContainer>
                                        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Total Logs</p>
                                            <p className="text-3xl font-black text-slate-800 tracking-tight">{totalRequests}</p>
                                        </div>
                                    </div>

                                    {/* Categories Indicators */}
                                    <div className="w-full md:w-1/2 grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                                        {dynamicChartData.map((item, index) => (
                                            <div key={index} className="flex items-center gap-3 p-3.5 bg-white/70 backdrop-blur-md rounded-2xl border border-slate-200/50 hover:border-blue-500/30 transition-all shadow-sm">
                                                <div className="w-3 h-3 rounded-full shrink-0 shadow-sm" style={{ backgroundColor: item.color }}></div>
                                                <div className="flex flex-col min-w-0">
                                                    <span className="text-xs font-bold text-slate-700 truncate">{item.name}</span>
                                                    <span className="text-[10px] font-black text-slate-400 uppercase mt-0.5">{item.value} Requests</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Right Side: Live Approval Tracker */}
                        <div className="lg:col-span-4 flex flex-col">
                            <div className="bg-gradient-to-br from-white to-slate-50/70 border border-slate-100 rounded-[32px] p-6 shadow-[0_15px_40px_rgba(0,0,0,0.03)] flex flex-col h-full relative overflow-visible">
                                <div className="flex justify-between items-center mb-8 border-b border-slate-100 pb-5">
                                    <div>
                                        <h3 className="text-base font-black text-slate-800 tracking-tight">Active Pipeline</h3>
                                        <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mt-0.5">Most recent application state</p>
                                    </div>
                                    {recentRequest && (
                                        <div className="flex items-center gap-1.5 px-3 py-1 bg-emerald-50 text-emerald-600 rounded-full border border-emerald-100/50 shadow-sm">
                                            <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse"></span>
                                            <span className="text-[8px] font-black uppercase tracking-wider">Active</span>
                                        </div>
                                    )}
                                </div>

                                {recentRequest ? (
                                    <div className="flex flex-col h-full justify-between">
                                        <div>
                                            {/* Header Detail Box */}
                                            <div className="flex items-center justify-between p-4 bg-white/80 border border-slate-200/60 rounded-2xl shadow-sm mb-8">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-9 h-9 rounded-xl bg-[#133A69] flex items-center justify-center shadow-md">
                                                        <FiFileText className="text-white w-4 h-4" />
                                                    </div>
                                                    <div>
                                                        <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">{recentRequest.displayId}</p>
                                                        <p className="text-xs font-black text-slate-800">{recentRequest.LeaveType}</p>
                                                    </div>
                                                </div>
                                                <span className={`px-2.5 py-1 rounded-xl text-[9px] font-black border uppercase tracking-wider ${recentRequest.status === 'PENDING' ? 'bg-amber-50 text-amber-700 border-amber-200/50' :
                                                        (recentRequest.status === 'APPROVED' || recentRequest.status === 'DIRECTLY APPROVED BY OWNER') ? 'bg-emerald-50 text-emerald-700 border-emerald-200/50' :
                                                            'bg-rose-50 text-rose-700 border-rose-200/50'
                                                    }`}>
                                                    {recentRequest.status}
                                                </span>
                                            </div>

                                            {/* Approval Timeline */}
                                            <div className="relative pl-10 space-y-10 before:absolute before:left-[19px] before:top-2 before:bottom-2 before:w-[2px] before:bg-slate-100">
                                                {recentRequest.approvers.map((app, index) => {
                                                    const isDone = app.status === 'approved' || app.status === 'approved by owner';
                                                    const isActive = app.status === 'pending';
                                                    const isRejected = app.status === 'rejected';

                                                    return (
                                                        <div key={index} className="relative group">
                                                            {/* Checkpoint Dot */}
                                                            <div className="absolute -left-[31px] top-1 z-10 flex items-center justify-center">
                                                                {isDone ? (
                                                                    <div className="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center shadow-md shadow-emerald-500/20 ring-4 ring-emerald-50">
                                                                        <FiCheck className="w-3.5 h-3.5 text-white stroke-[4]" />
                                                                    </div>
                                                                ) : isActive ? (
                                                                    <div className="relative">
                                                                        <div className="absolute -inset-1.5 bg-amber-400/20 rounded-full animate-ping"></div>
                                                                        <div className="w-6 h-6 rounded-full bg-amber-500 flex items-center justify-center shadow-md shadow-amber-500/20 ring-4 ring-amber-50 relative z-10">
                                                                            <FiClock className="w-3.5 h-3.5 text-white" />
                                                                        </div>
                                                                    </div>
                                                                ) : isRejected ? (
                                                                    <div className="w-6 h-6 rounded-full bg-rose-500 flex items-center justify-center shadow-md shadow-rose-500/20 ring-4 ring-rose-50">
                                                                        <FiXCircle className="w-3.5 h-3.5 text-white" />
                                                                    </div>
                                                                ) : (
                                                                    <div className="w-5 h-5 rounded-full border-2 border-slate-200 bg-white ring-4 ring-white"></div>
                                                                )}
                                                            </div>

                                                            <div className={`transition-all duration-300 ${!isActive && !isDone && !isRejected ? 'opacity-30' : ''}`}>
                                                                <div className="flex flex-col">
                                                                    <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{app.role}</span>
                                                                    <span className="text-sm font-bold text-slate-700 mt-0.5">{app.name}</span>
                                                                </div>
                                                                <span className={`inline-block mt-2 px-2.5 py-0.5 rounded-lg text-[9px] font-black border uppercase tracking-wider ${isDone ? 'bg-emerald-50 text-emerald-700 border-emerald-100/50' :
                                                                        isActive ? 'bg-amber-50 text-amber-700 border-amber-100/50' :
                                                                            isRejected ? 'bg-rose-50 text-rose-700 border-rose-100/50' :
                                                                                'bg-slate-50 text-slate-400 border-slate-200/50'
                                                                    }`}>
                                                                    {app.status}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>

                                        {recentRequest.status === 'PENDING' && (
                                            <button
                                                onClick={() => openWithdrawModal(recentRequest.id, recentRequest.type)}
                                                className="w-full mt-10 py-3.5 bg-rose-50 text-rose-600 border border-rose-200/50 font-bold text-xs rounded-2xl hover:bg-rose-100 hover:text-rose-700 transition-all flex items-center justify-center gap-2 active:scale-[0.98] cursor-pointer shadow-sm shadow-rose-500/5"
                                            >
                                                <FiArrowLeft />
                                                WITHDRAW APPLICATION
                                            </button>
                                        )}
                                    </div>
                                ) : (
                                    <div className="flex-grow flex flex-col items-center justify-center text-center p-8 bg-slate-50 border-2 border-dashed border-slate-200 rounded-3xl">
                                        <div className="w-14 h-14 rounded-2xl bg-white border border-slate-100 flex items-center justify-center shadow-sm mb-3">
                                            <FiFileText className="text-slate-350 w-7 h-7" />
                                        </div>
                                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">No Active Pipeline</p>
                                        <p className="text-[11px] text-slate-400/80 font-medium mt-1">There are no pending applications under review.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'All Requests' && (
                    <div className="bg-gradient-to-br from-white to-slate-50/70 border border-slate-100 rounded-[32px] shadow-[0_15px_40px_rgba(0,0,0,0.03)] p-6 md:p-8 relative overflow-hidden min-h-[600px]">
                        
                        {/* Filters Controls Header */}
                        <div className="border-b border-slate-100 pb-6 mb-6">
                            <div className="flex flex-col xl:flex-row justify-between items-start xl:items-end gap-5">
                                <div className="space-y-3 w-full xl:max-w-[400px]">
                                    <div className="flex items-center gap-3">
                                        <span className="p-2 bg-blue-50/80 text-blue-600 rounded-xl flex items-center justify-center shadow-sm">
                                            <FiList size={18} />
                                        </span>
                                        <div>
                                            <h3 className="text-lg font-black text-slate-800 tracking-tight">Application History</h3>
                                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mt-0.5">Filter and review previous leave records</p>
                                        </div>
                                    </div>
                                    <div className="relative group">
                                        <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-blue-500 transition-colors" size={16} />
                                        <input
                                            type="text"
                                            placeholder="Search by Request ID, type, or reason..."
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            className="w-full pl-11 pr-5 py-3 bg-white border border-slate-200 rounded-2xl outline-none focus:border-blue-500/50 focus:ring-4 focus:ring-blue-500/5 text-xs font-bold text-slate-700 transition-all placeholder:text-slate-400 shadow-sm"
                                        />
                                    </div>
                                </div>

                                <div className="flex flex-wrap gap-4 w-full xl:w-auto">
                                    {/* Month Selector */}
                                    <div className="space-y-1.5 flex-1 min-w-[140px]">
                                        <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest px-1">Selected Month</span>
                                        <div className="relative">
                                            <select
                                                value={selectedMonth}
                                                onChange={(e) => setSelectedMonth(e.target.value)}
                                                className="w-full appearance-none bg-white border border-slate-200 text-slate-700 text-xs font-bold pl-4 pr-10 py-3 rounded-2xl outline-none cursor-pointer hover:bg-slate-50 transition-all shadow-sm focus:border-blue-500/50"
                                            >
                                                <option value="All Months">All Records</option>
                                                {monthOptions.map((m) => (
                                                    <option key={m.value} value={m.value}>{m.label}</option>
                                                ))}
                                            </select>
                                            <FiChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={14} />
                                        </div>
                                    </div>

                                    {/* Type Filter */}
                                    <div className="space-y-1.5 flex-1 min-w-[140px]">
                                        <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest px-1">Leave Type</span>
                                        <div className="relative">
                                            <select
                                                value={typeFilter}
                                                onChange={(e) => setTypeFilter(e.target.value)}
                                                className="w-full appearance-none bg-white border border-slate-200 text-slate-700 text-xs font-bold pl-4 pr-10 py-3 rounded-2xl outline-none cursor-pointer hover:bg-slate-50 transition-all shadow-sm focus:border-blue-500/50"
                                            >
                                                <option>All Types</option>
                                                <option>Advance Leave</option>
                                                <option>Half Day Leave</option>
                                                <option>Early Leave</option>
                                                <option>Late Arrival</option>
                                                <option>Time Break</option>
                                                <option>Emergency Leave</option>
                                            </select>
                                            <FiChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={14} />
                                        </div>
                                    </div>

                                    {/* Status Filter */}
                                    <div className="space-y-1.5 flex-1 min-w-[140px]">
                                        <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest px-1">Status</span>
                                        <div className="relative">
                                            <select
                                                value={statusFilter}
                                                onChange={(e) => setStatusFilter(e.target.value)}
                                                className="w-full appearance-none bg-white border border-slate-200 text-slate-700 text-xs font-bold pl-4 pr-10 py-3 rounded-2xl outline-none cursor-pointer hover:bg-slate-50 transition-all shadow-sm focus:border-blue-500/50"
                                            >
                                                <option>Any Status</option>
                                                <option>Approved</option>
                                                <option>Pending</option>
                                                <option>Rejected</option>
                                                <option>Withdrawn</option>
                                            </select>
                                            <FiChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={14} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                         {/* List View styled like a beautiful table */}
                        <div className="bg-white border border-slate-100 rounded-2xl shadow-[0_4px_25px_rgba(0,0,0,0.01)] overflow-hidden relative z-0">
                            <div className="w-full overflow-x-auto">
                                <table className="min-w-full border-collapse text-left text-sm">
                                    <thead>
                                        <tr className="bg-[#133A69] text-white font-semibold border-b border-slate-200 uppercase tracking-wider text-xs">
                                            <th className="py-3 px-4 text-center w-[80px]">Expand</th>
                                            <th className="py-3 px-4">ID & Type</th>
                                            <th className="py-3 px-4">Applied Period</th>
                                            <th className="py-3 px-4">Duration</th>
                                            <th className="py-3 px-4">Status</th>
                                            <th className="py-3 px-4 text-right">Applied On</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100 bg-white">
                                        {filteredRequestsList.length === 0 ? (
                                            <tr>
                                                <td colSpan="6" className="py-20 text-center text-slate-400 font-bold bg-slate-50/20">
                                                    <div className="flex flex-col items-center justify-center gap-3">
                                                        <FiBox className="w-10 h-10 text-slate-350" />
                                                        <p className="font-black text-sm text-slate-500 uppercase tracking-widest">No Applications Found</p>
                                                        <p className="text-xs text-slate-450 font-medium">There are no records matching the selected parameters.</p>
                                                    </div>
                                                </td>
                                            </tr>
                                        ) : filteredRequestsList.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((req) => {
                                            const isExpanded = expandedRequest === req.id;
                                            return (
                                                <React.Fragment key={req.id}>
                                                    <tr
                                                        className={`hover:bg-slate-50/40 transition-all cursor-pointer group ${isExpanded ? 'bg-slate-50/50' : ''}`}
                                                        onClick={() => toggleExpand(req.id)}
                                                    >
                                                        <td className="py-3 px-4 text-center">
                                                            <div className={`w-8 h-8 rounded-lg mx-auto flex items-center justify-center transition-all border ${isExpanded ? 'bg-[#133A69] text-white border-[#133A69]' : 'bg-white text-slate-450 border-slate-200/60 group-hover:bg-slate-55 shadow-xs'}`}>
                                                                {isExpanded ? <FiChevronUp size={14} /> : <FiChevronDown size={14} />}
                                                            </div>
                                                        </td>
                                                        <td className="py-3 px-4 font-semibold text-slate-800">
                                                            <div className="flex items-center gap-3">
                                                                <div className="w-1.5 h-7 rounded-full shrink-0" style={{ backgroundColor: colorMap[req.LeaveType] || '#cbd5e1' }}></div>
                                                                <div>
                                                                    <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider block leading-none mb-0.5">{req.displayId}</span>
                                                                    <span className="text-sm font-semibold text-slate-800">{req.LeaveType}</span>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="py-3 px-4 font-semibold text-slate-800">
                                                            <div>
                                                                <p className="text-sm font-semibold text-slate-800 leading-none mb-1">{req.dateRange}</p>
                                                                <div className="flex items-center gap-1.5 text-slate-650 text-xs font-medium">
                                                                    <FiCalendar className="w-3.5 h-3.5 shrink-0" />
                                                                    <span className="uppercase tracking-wider">{req.dateSub}</span>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="py-3 px-4 font-semibold text-slate-800 text-sm">
                                                            <div>
                                                                <p className="leading-none mb-1 font-semibold">{req.duration}</p>
                                                                {req.halfType && <span className="text-[10px] text-blue-800 font-bold uppercase bg-blue-50 px-1.5 py-0.5 rounded border border-blue-200">{req.halfType}</span>}
                                                            </div>
                                                        </td>
                                                        <td className="py-3 px-4">
                                                            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border uppercase tracking-wider ${req.status === 'PENDING' ? 'bg-amber-50 text-amber-800 border-amber-250/50' :
                                                                    (req.status === 'APPROVED' || req.status === 'DIRECTLY APPROVED BY OWNER') ? 'bg-emerald-50 text-emerald-800 border-emerald-250/50' :
                                                                        'bg-rose-50 text-rose-800 border-rose-250/50'
                                                                }`}>
                                                                <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${req.status === 'PENDING' ? 'bg-amber-500' :
                                                                        (req.status === 'APPROVED' || req.status === 'DIRECTLY APPROVED BY OWNER') ? 'bg-emerald-500' :
                                                                            'bg-rose-500'
                                                                    }`}></span>
                                                                {req.status}
                                                            </span>
                                                        </td>
                                                        <td className="py-3 px-4 text-right font-medium text-slate-600 text-xs uppercase tracking-wider">
                                                            {req.appliedOn}
                                                        </td>
                                                    </tr>

                                                    {/* Expanded Panel (Premium UI) */}
                                                    {isExpanded && (
                                                        <tr>
                                                            <td colSpan="6" className="p-0 border-none bg-slate-50/30">
                                                                <div className="p-6 md:p-8 bg-slate-50/40 border-y border-slate-100 flex flex-col gap-6 md:flex-row md:justify-between">
                                                                    
                                                                    {/* Left side: Context & Reason */}
                                                                    <div className="flex-1 max-w-[55%]">
                                                                        <h4 className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-3 flex items-center gap-2">
                                                                            <div className="w-1.5 h-3.5 bg-blue-500 rounded-full"></div>
                                                                            Application Reason
                                                                        </h4>
                                                                        <div className="bg-white rounded-2xl p-5 border border-slate-200/60 shadow-sm">
                                                                            <p className="text-sm text-slate-800 italic font-medium leading-relaxed">
                                                                                "{req.reason}"
                                                                            </p>
                                                                            {req.status === 'PENDING' && (
                                                                                <div className="mt-5 pt-4 border-t border-slate-100 flex items-center justify-between">
                                                                                    <span className="text-xs text-slate-500 font-medium italic">Awaiting pipeline verification...</span>
                                                                                    <button
                                                                                        onClick={(e) => { e.stopPropagation(); openWithdrawModal(req.id, req.type); }}
                                                                                        className="px-4 py-2 rounded-xl bg-rose-55 text-rose-700 border border-rose-200/50 text-xs font-bold uppercase tracking-wider hover:bg-rose-500 hover:text-white transition-all shadow-xs active:scale-95 flex items-center gap-1.5 cursor-pointer"
                                                                                    >
                                                                                        <FiRotateCcw size={12} />
                                                                                        Withdraw
                                                                                    </button>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </div>

                                                                    {/* Right side: Pipeline Timeline */}
                                                                    <div className="w-full md:max-w-[40%] shrink-0">
                                                                        <h4 className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-5 flex items-center gap-2">
                                                                            <div className="w-1.5 h-3.5 bg-indigo-500 rounded-full"></div>
                                                                            Approval Journey
                                                                        </h4>
                                                                        <div className="space-y-8 pl-8 relative before:absolute before:left-[17px] before:top-2 before:bottom-2 before:w-[2px] before:bg-slate-200/60">
                                                                            {req.approvers.map((app, appIdx) => {
                                                                                const isAppr = app.status === 'approved' || app.status === 'approved by owner';
                                                                                const isPend = app.status === 'pending';
                                                                                const isRej = app.status === 'rejected';

                                                                                return (
                                                                                    <div key={appIdx} className="relative group/step">
                                                                                        {/* Line Connector Dot */}
                                                                                        <div className="absolute -left-[27px] top-0.5 z-10 flex items-center justify-center">
                                                                                            {isAppr ? (
                                                                                                <div className="w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center shadow-md ring-4 ring-emerald-50">
                                                                                                    <FiCheck className="text-white w-3 h-3 stroke-[4]" />
                                                                                                </div>
                                                                                            ) : isPend ? (
                                                                                                <div className="relative">
                                                                                                    <div className="absolute -inset-1 bg-amber-400/20 rounded-full animate-ping"></div>
                                                                                                    <div className="w-5 h-5 rounded-full bg-white border-2 border-amber-400 flex items-center justify-center relative z-10">
                                                                                                        <div className="w-1.5 h-1.5 bg-amber-400 rounded-full"></div>
                                                                                                    </div>
                                                                                                </div>
                                                                                            ) : isRej ? (
                                                                                                <div className="w-5 h-5 rounded-full bg-rose-500 flex items-center justify-center shadow-md ring-4 ring-rose-50">
                                                                                                    <FiXCircle className="text-white w-3 h-3" />
                                                                                                </div>
                                                                                            ) : (
                                                                                                <div className="w-4 h-4 rounded-full border-2 border-slate-200 bg-white ring-4 ring-slate-100"></div>
                                                                                            )}
                                                                                        </div>
                                                                                        <div className={!isAppr && !isPend && !isRej ? 'opacity-35' : ''}>
                                                                                            <div className="flex justify-between items-start gap-4">
                                                                                                <div>
                                                                                                    <p className="text-xs font-bold text-slate-500 uppercase tracking-widest leading-none mb-1">{app.role}</p>
                                                                                                    <h5 className="text-xs font-semibold text-slate-800">{app.name}</h5>
                                                                                                </div>
                                                                                                <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded border tracking-wider shrink-0 ${isAppr ? 'bg-emerald-50 text-emerald-800 border-emerald-200' :
                                                                                                        isPend ? 'bg-amber-50 text-amber-800 border-amber-200' :
                                                                                                            isRej ? 'bg-rose-50 text-rose-800 border-rose-200' :
                                                                                                                'bg-slate-55 text-slate-500 border-slate-200'
                                                                                                    }`}>
                                                                                                    {app.status}
                                                                                                </span>
                                                                                            </div>
                                                                                        </div>
                                                                                    </div>
                                                                                )
                                                                            })}
                                                                        </div>
                                                                    </div>

                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )}
                                                </React.Fragment>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                            <TablePagination
                                component="div"
                                count={filteredRequestsList.length}
                                page={page}
                                onPageChange={handleChangePage}
                                rowsPerPage={rowsPerPage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                rowsPerPageOptions={[5, 10, 25, 50]}
                                className="bg-white border-t border-slate-100 rounded-[0px_0px_24px_24px]"
                            />
                        </div>
                    </div>
                )}
            </div>

            {/* Premium Withdraw Modal */}
            {isWithdrawModalOpen && (
                <div className="fixed inset-0 bg-slate-900/60 z-[100] flex items-center justify-center p-4 backdrop-blur-md transition-all duration-300">
                    <div className="bg-white rounded-[32px] p-8 max-w-[400px] w-full shadow-2xl flex flex-col items-center text-center relative overflow-hidden border border-slate-100 animate-in zoom-in-95 duration-300">
                        <div className="absolute top-0 right-0 w-28 h-28 bg-rose-50/50 rounded-full -mr-14 -mt-14"></div>

                        <div className="w-[72px] h-[72px] rounded-2xl bg-rose-50 text-rose-600 flex items-center justify-center mb-6 shadow-sm border border-rose-100 relative z-10 group">
                            <FiRotateCcw className="w-8 h-8 stroke-[2.5] group-hover:rotate-[-45deg] transition-transform duration-550" />
                        </div>

                        <h3 className="text-xl font-black text-slate-800 mb-2 relative z-10">
                            Recall Application?
                        </h3>

                        <p className="text-slate-500 text-sm leading-relaxed mb-8 px-2 relative z-10">
                            Are you sure you want to withdraw your <strong className="text-slate-850 font-bold">{withdrawRequest?.type || 'Leave'}</strong>? This request will be permanently cancelled.
                        </p>

                        <div className="w-full flex flex-col gap-2 relative z-10">
                            <button
                                className="w-full bg-[#133A69] hover:bg-slate-800 text-white text-xs font-black uppercase tracking-wider py-4 px-6 rounded-2xl shadow-lg transition-all active:scale-95 cursor-pointer"
                                onClick={confirmWithdraw}
                            >
                                Yes, Recall Request
                            </button>

                            <button
                                onClick={closeWithdrawModal}
                                className="w-full py-3 text-slate-400 font-bold text-xs hover:text-slate-700 transition-colors uppercase tracking-wider cursor-pointer"
                            >
                                No, keep it active
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MyRequest;
